import { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { CARD_CLASS } from "../styles";
import { CHART_SERIES_COLORS } from "../constants";
import type { ChartData } from "../types";

type Props = {
  data: ChartData;
  palette: string[];
};

export default function ChartSection({ data, palette }: Props) {
  const hasData = data.dates.length > 0 && data.series.length > 0;
  const chartKey = useMemo(
    () =>
      JSON.stringify({
        dates: data.dates,
        series: data.series.map((series) => ({ name: series.name, data: series.data, style: series.style, width: series.width })),
      }),
    [data],
  );
  const resolveSeriesColor = (name: string, index: number) => {
    const lower = name.toLowerCase();
    const group = lower.includes("конкурент") ? "competitor" : "client";
    const bucket = lower.includes("миним") ? "min" : lower.includes("максим") ? "max" : "avg";
    const mapped = CHART_SERIES_COLORS[group][bucket];
    return mapped ?? palette[index % palette.length];
  };

  const option = useMemo(
    () => ({
      color: data.series.map((series, index) => resolveSeriesColor(series.name, index)),
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "line" },
        valueFormatter: (value: number | null | undefined) =>
          typeof value === "number" ? value.toLocaleString("ru-RU") : "—",
        backgroundColor: "rgba(15,23,42,0.92)",
        borderColor: "#1e293b",
        textStyle: { color: "#f8fafc", fontSize: 12 },
      },
      legend: {
        top: 8,
        itemWidth: 14,
        itemHeight: 14,
        icon: "rect",
        textStyle: { color: "#64748b", fontSize: 12 },
      },
      grid: { left: 16, right: 24, top: 52, bottom: 28, containLabel: true },
      xAxis: {
        type: "category",
        data: data.dates,
        boundaryGap: false,
        name: "Дата заезда",
        nameLocation: "middle",
        nameGap: 26,
        nameTextStyle: { color: "#94a3b8", fontSize: 12 },
        axisLine: { lineStyle: { color: "#e2e8f0" } },
        axisLabel: { color: "#94a3b8" },
        axisTick: { show: false },
      },
      yAxis: {
        type: "value",
        axisLine: { show: false },
        axisLabel: { color: "#94a3b8" },
        splitLine: { lineStyle: { color: "#f1f5f9" } },
      },
      series: data.series.map((series, index) => ({
        name: series.name,
        type: "line",
        data: series.data,
        lineStyle: {
          color: resolveSeriesColor(series.name, index),
          width: series.width ?? (series.style === "dashed" ? 2 : 2.4),
          type: series.style ?? "solid",
        },
        symbol: "circle",
        symbolSize: 6,
        smooth: true,
        itemStyle: {
          color: resolveSeriesColor(series.name, index),
        },
      })),
    }),
    [data, palette],
  );

  return (
    <section className={CARD_CLASS}>
      <div className="flex items-center gap-3">
        <span className="h-6 w-1.5 rounded-full bg-emerald-500" />
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Динамика цен</h2>
          <p className="text-sm text-slate-500">Сравнение минимальных, средних и максимальных значений</p>
        </div>
      </div>

      <div className="mt-4">
        {hasData ? (
          <ReactECharts key={chartKey} option={option} notMerge={true} style={{ height: 360, width: "100%" }} />
        ) : (
          <div className="grid h-[360px] place-items-center rounded-2xl border border-dashed border-slate-200 bg-slate-50/70 text-sm text-slate-500">
            Нет данных для построения графика
          </div>
        )}
      </div>
    </section>
  );
}
